import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { SearchSelect } from "../ui/SearchSelect";
import { trpc } from "../../lib/trpc";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

/** TR sayı: 1.234 / 1.234,56 → nokta binlik, virgül ondalık (ShipmentDetailDrawer.parseTrNumber ile aynı). */
function parseTrNumber(raw: string): number {
  const s = raw.trim();
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(s)) return Number(s.replace(/\./g, "").replace(",", "."));
  if (s.includes(",")) return Number(s.replace(/\./g, "").replace(",", "."));
  return Number(s);
}
function fmtQty(n: number): string { return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n); }

interface PlanLine { lotId: string; lotCode: string; itemName: string; qty: number; uom: string; available: number; }

/** Yeni transfer planı: kaynak/hedef depo + tarih/saat + opsiyonel lot satırları. */
export function CreateTransferDrawer({ open, onClose, onCreated }: Props) {
  const qc = useQueryClient();
  // Kaynak = yalnız aktif tesis (kendi stoğun); hedef = tüm tesisler (çapraz-tesis transfer mümkün).
  const fromLocsQ = trpc.warehouse.transferableLocations.useQuery({ role: "from" }, { enabled: open });
  const toLocsQ = trpc.warehouse.transferableLocations.useQuery({ role: "to" }, { enabled: open });
  const createMut = trpc.warehouse.create.useMutation();

  const [fromId, setFromId] = useState<string | null>(null);
  const [toId, setToId] = useState<string | null>(null);
  const [plannedDate, setPlannedDate] = useState("");
  const [plannedTime, setPlannedTime] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<PlanLine[]>([]);
  const [lotPick, setLotPick] = useState<string | null>(null);
  const [lotQty, setLotQty] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const sourceLotsQ = trpc.warehouse.sourceLots.useQuery({ fromLocationId: fromId ?? "" }, { enabled: open && !!fromId });

  useEffect(() => {
    if (open) { setFromId(null); setToId(null); setPlannedDate(""); setPlannedTime(""); setNotes(""); setLines([]); setLotPick(null); setLotQty(""); setErr(null); createMut.reset(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // Kaynak değişince satırlar geçersiz olur → temizle.
  useEffect(() => { setLines([]); setLotPick(null); setLotQty(""); }, [fromId]);

  const fromOpts = useMemo(() => (fromLocsQ.data ?? []).map((l) => ({ value: l.id, label: l.name, desc: l.code })), [fromLocsQ.data]);
  const toOpts = useMemo(() => (toLocsQ.data ?? []).map((l) => ({ value: l.id, label: l.name, desc: l.code })), [toLocsQ.data]);
  const lotOpts = useMemo(() => {
    const taken = new Set(lines.map((l) => l.lotId));
    return (sourceLotsQ.data ?? []).filter((l) => !taken.has(l.lotId)).map((l) => ({ value: l.lotId, label: l.itemName, desc: l.lotCode, tail: `${fmtQty(l.available)} ${l.uom}` }));
  }, [sourceLotsQ.data, lines]);

  function addLine() {
    setErr(null);
    if (!lotPick) return;
    const lot = sourceLotsQ.data?.find((l) => l.lotId === lotPick);
    if (!lot) return;
    const n = lotQty.trim() ? parseTrNumber(lotQty) : NaN;
    if (!Number.isFinite(n) || n <= 0) return setErr("Geçerli bir miktar girin.");
    if (n > lot.available + 0.0001) return setErr(`En fazla ${fmtQty(lot.available)} ${lot.uom} mevcut.`);
    setLines((p) => [...p, { lotId: lot.lotId, lotCode: lot.lotCode, itemName: lot.itemName, qty: n, uom: lot.uom, available: lot.available }]);
    setLotPick(null); setLotQty("");
  }
  function removeLine(lotId: string) { setLines((p) => p.filter((l) => l.lotId !== lotId)); }

  async function submit() {
    setErr(null);
    if (!fromId || !toId) return setErr("Kaynak ve hedef depo seçin.");
    if (fromId === toId) return setErr("Kaynak ve hedef depo aynı olamaz.");
    try {
      const res = await createMut.mutateAsync({
        fromLocationId: fromId, toLocationId: toId,
        plannedDate: plannedDate ? new Date(plannedDate + "T00:00:00").toISOString() : undefined,
        plannedTime: plannedTime || undefined,
        notes: notes.trim() || undefined,
        lines: lines.length ? lines.map((l) => ({ lotId: l.lotId, qty: l.qty })) : undefined,
      });
      await qc.invalidateQueries();
      onClose();
      onCreated(res.id);
    } catch (e) { setErr((e as Error).message); }
  }

  return (
    <Drawer open={open} onClose={onClose} size="lg" title="Yeni Transfer Planı" subtitle="Kaynak → hedef depo + (opsiyonel) taşınacak lotlar"
      footer={<>
        <button type="button" className="btn-md ghost" onClick={onClose}>Vazgeç</button>
        <button type="button" className="btn-md primary" onClick={submit} disabled={createMut.isPending || !fromId || !toId}><Icon name="plus" size={14} />{createMut.isPending ? "Oluşturuluyor…" : "Plan Oluştur"}</button>
      </>}>
      {err ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{err}</div> : null}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="form-row" style={{ gap: 12 }}>
          <div className="form-field"><label className="lbl">Kaynak Depo <span className="req">*</span></label>
            <SearchSelect value={fromId} options={fromOpts.filter((o) => o.value !== toId)} onChange={setFromId} icon="warehouse" placeholder="Kaynak depo seç…" searchPlaceholder="Depo ara…" emptyText="Transfer edilir depo yok" />
          </div>
          <div className="form-field"><label className="lbl">Hedef Depo <span className="req">*</span></label>
            <SearchSelect value={toId} options={toOpts.filter((o) => o.value !== fromId)} onChange={setToId} icon="warehouse" placeholder="Hedef depo seç…" searchPlaceholder="Depo ara…" emptyText="Transfer edilir depo yok" />
          </div>
        </div>
        <div className="form-row" style={{ gap: 12 }}>
          <div className="form-field"><label className="lbl">Planlanan Tarih</label><input className="form-input" type="date" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)} /></div>
          <div className="form-field"><label className="lbl">Planlanan Saat</label><input className="form-input" type="time" value={plannedTime} onChange={(e) => setPlannedTime(e.target.value)} /></div>
        </div>
        <div className="form-field"><label className="lbl">Not</label><textarea className="form-textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} placeholder="Transfer notu (opsiyonel)" /></div>

        {/* Opsiyonel lot satırları — girilirse tamamlanınca stok taşınır */}
        <div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
            <span className="mo-sec-title" style={{ fontSize: 12.5, fontWeight: 600 }}>Taşınacak Lotlar ({lines.length})</span>
            <span style={{ fontSize: 11, color: "var(--neutral-500)" }}>opsiyonel — girilirse tamamlanınca stok kaynak→hedef taşınır</span>
          </div>
          {lines.length > 0 ? (
            <table className="dt2" style={{ marginBottom: 8 }}>
              <thead><tr><th>Ürün</th><th>Lot</th><th className="num">Miktar</th><th style={{ width: 40 }} /></tr></thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.lotId}>
                    <td>{l.itemName}</td>
                    <td className="cell-id" style={{ fontSize: 11 }}>{l.lotCode}</td>
                    <td className="cell-num">{fmtQty(l.qty)} <span style={{ fontSize: 10, color: "var(--neutral-400)" }}>{l.uom}</span></td>
                    <td><div className="row-acts"><button type="button" className="iact danger" aria-label="Satırı kaldır" onClick={() => removeLine(l.lotId)}><Icon name="x" size={14} /></button></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: "8px 12px", border: "1px dashed var(--neutral-300)", borderRadius: "var(--radius-md)", background: "var(--neutral-50)", fontSize: 12, color: "var(--neutral-500)", marginBottom: 8 }}>
              {fromId ? "Lot eklenmezse yalnız lojistik/ağırlık kaydı olur (stok taşınmaz)." : "Önce kaynak depo seçin."}
            </div>
          )}
          {fromId ? (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 220px", minWidth: 180 }}><SearchSelect value={lotPick} options={lotOpts} onChange={setLotPick} icon="boxes" placeholder="Kaynak depodaki lot…" searchPlaceholder="Ürün / lot ara…" emptyText={sourceLotsQ.isLoading ? "Yükleniyor…" : "Kaynak depoda stok yok"} /></div>
              <input className="form-input" style={{ width: 96 }} type="text" inputMode="decimal" value={lotQty} onChange={(e) => setLotQty(e.target.value.replace(/[^\d.,]/g, ""))} placeholder="miktar" aria-label="Miktar" />
              <button type="button" className="btn-md secondary" onClick={addLine} disabled={!lotPick}><Icon name="plus" size={14} />Ekle</button>
            </div>
          ) : null}
        </div>
      </div>
    </Drawer>
  );
}
